import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Snackbar,
    Alert,
    Stack
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    CheckCircle as CheckCircleIcon,
    Schedule as ScheduleIcon,
    Notifications as NotificationsIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    Feedback as FeedbackIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { Campaign } from '../../../../types/Campaign';

interface TimelineEventsDatesProps {
    campaign: Campaign;
    onSave?: (updatedCampaign: Partial<Campaign>) => Promise<Campaign>;
}

interface CampaignEvent {
    id: string;
    field: keyof Campaign;
    label: string;
    description: string;
    value: string | null | undefined;
    color: string;
    icon: React.ReactNode;
    required: boolean;
    order: number; // Fixed order that cannot change
}

const TimelineEventsDates: React.FC<TimelineEventsDatesProps> = ({ campaign, onSave }) => {
    const theme = useTheme();
    const timelineRef = useRef<HTMLDivElement>(null);
    const [draggedEvent, setDraggedEvent] = useState<CampaignEvent | null>(null);
    const [dragPosition, setDragPosition] = useState<{ x: number; y: number } | null>(null);
    const [dragPreviewDate, setDragPreviewDate] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState<number>(1); // 1 = normal, 2 = zoomed in, 0.5 = zoomed out
    
    // Error handling state
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [showError, setShowError] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState<boolean>(false);

    // Fixed order events (cannot be reordered)
    const campaignEvents: CampaignEvent[] = [
        {
            id: 'applications_open',
            field: 'applications_open',
            label: 'Applications Open',
            description: 'Start accepting applications',
            value: campaign.applications_open,
            color: theme.palette.primary.main,
            icon: <CalendarIcon />,
            required: true,
            order: 1
        },
        {
            id: 'applications_close',
            field: 'applications_close',
            label: 'Applications Close',
            description: 'Application deadline',
            value: campaign.applications_close,
            color: theme.palette.primary.dark,
            icon: <ScheduleIcon />,
            required: true,
            order: 2
        },
        {
            id: 'match_notification_date',
            field: 'match_notification_date',
            label: 'Matches Sent',
            description: 'Notify participants of matches',
            value: campaign.match_notification_date,
            color: theme.palette.warning.main,
            icon: <NotificationsIcon />,
            required: true,
            order: 3
        },
        {
            id: 'matches_finalized',
            field: 'matches_finalized',
            label: 'Matches Finalized',
            description: 'Deadline to acknowledge matches',
            value: campaign.matches_finalized,
            color: theme.palette.warning.dark,
            icon: <CheckCircleIcon />,
            required: true,
            order: 4
        },
        {
            id: 'kickoff',
            field: 'kickoff',
            label: 'Program Kickoff',
            description: 'Official program start',
            value: campaign.kickoff,
            color: theme.palette.success.main,
            icon: <PlayArrowIcon />,
            required: true,
            order: 5
        },
        {
            id: 'closing',
            field: 'closing',
            label: 'Program Closing',
            description: 'Final program date',
            value: campaign.closing,
            color: theme.palette.success.dark,
            icon: <StopIcon />,
            required: true,
            order: 6
        },
        {
            id: 'feedback_deadline',
            field: 'feedback_deadline',
            label: 'Feedback Due',
            description: 'Feedback submission deadline',
            value: campaign.feedback_deadline,
            color: theme.palette.info.main,
            icon: <FeedbackIcon />,
            required: false,
            order: 7
        }
    ];

    // Generate timeline dates (every day for a year)
    const generateTimelineDates = () => {
        const dates = [];
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30); // Start 30 days ago
        
        for (let i = 0; i < 365; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            dates.push(date.toISOString().split('T')[0]);
        }
        return dates;
    };

    const timelineDates = generateTimelineDates();
    const daySpacing = 15 * zoomLevel; // Base spacing of 15px multiplied by zoom level

    const isPastDate = useCallback((dateStr: string): boolean => {
        const date = new Date(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Reset time to start of day
        date.setHours(0, 0, 0, 0);
        return date < today;
    }, []);

    const handleDateUpdate = useCallback(async (field: keyof Campaign, newDate: string) => {
        if (onSave) {
            try {
                await onSave({ [field]: newDate });
                setSuccessMessage('Date updated successfully');
                setShowSuccess(true);
                setErrorMessage(null);
                setShowError(false);
            } catch (e: any) {
                console.error('Error saving date', e);
                
                let errorMsg = 'Failed to update date. Please try again.';
                if (e?.response?.data?.detail) {
                    errorMsg = e.response.data.detail;
                } else if (e?.message) {
                    errorMsg = e.message;
                }
                
                setErrorMessage(errorMsg);
                setShowError(true);
            }
        }
    }, [onSave]);

    const handleMouseDown = (event: React.MouseEvent, campaignEvent: CampaignEvent) => {
        setDraggedEvent(campaignEvent);
        setDragPosition({ x: event.clientX, y: event.clientY });
    };

    const handleMouseMove = useCallback((event: MouseEvent) => {
        if (draggedEvent && dragPosition && timelineRef.current) {
            setDragPosition({ x: event.clientX, y: event.clientY });
            
            // Calculate preview date in real-time
            const timelineContainer = timelineRef.current.parentElement;
            if (timelineContainer) {
                const containerRect = timelineContainer.getBoundingClientRect();
                const relativeY = event.clientY - containerRect.top + timelineContainer.scrollTop;
                
                // Calculate which date index this Y position corresponds to
                const dateIndex = Math.round(relativeY / daySpacing);
                const clampedIndex = Math.max(0, Math.min(dateIndex, timelineDates.length - 1));
                const previewDate = timelineDates[clampedIndex];
                
                setDragPreviewDate(previewDate);
            }
        }
    }, [draggedEvent, dragPosition, daySpacing, timelineDates]);

    const handleMouseUp = useCallback((event: MouseEvent) => {
        if (draggedEvent && timelineRef.current) {
            const timelineContainer = timelineRef.current.parentElement;
            if (!timelineContainer) return;
            
            const containerRect = timelineContainer.getBoundingClientRect();
            const relativeY = event.clientY - containerRect.top + timelineContainer.scrollTop;
            
            // Calculate which date index this Y position corresponds to
            const dateIndex = Math.round(relativeY / daySpacing);
            const clampedIndex = Math.max(0, Math.min(dateIndex, timelineDates.length - 1));
            const closestDate = timelineDates[clampedIndex];
            
            if (closestDate && closestDate !== draggedEvent.value) {
                // Check if the target date is in the past
                if (isPastDate(closestDate)) {
                    setErrorMessage('Cannot set event date to a past date');
                    setShowError(true);
                } else {
                    handleDateUpdate(draggedEvent.field, closestDate);
                }
            }
        }
        
        setDraggedEvent(null);
        setDragPosition(null);
        setDragPreviewDate(null);
    }, [draggedEvent, handleDateUpdate, daySpacing, timelineDates, isPastDate]);

    useEffect(() => {
        if (draggedEvent) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [draggedEvent, handleMouseMove, handleMouseUp]);

    const formatDate = (dateStr: string): string => {
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    const isToday = (dateStr: string): boolean => {
        const today = new Date().toISOString().split('T')[0];
        return dateStr === today;
    };

    const isSunday = (dateStr: string): boolean => {
        const date = new Date(dateStr);
        return date.getDay() === 0; // Sunday is 0
    };



    const getEventAtDate = (date: string): CampaignEvent | null => {
        return campaignEvents.find(event => event.value === date) || null;
    };

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev * 1.5, 4)); // Max zoom 4x
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev / 1.5, 0.25)); // Min zoom 0.25x
    };

    return (
        <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 2, fontWeight: 600 }}>
                Campaign Timeline
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography variant="body1" color="text.secondary">
                    Drag event arrows up and down the timeline to adjust their dates. Events maintain their logical order.
                </Typography>
                
                {/* Zoom Controls */}
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                        Zoom:
                    </Typography>
                    <Box
                        onClick={handleZoomOut}
                        sx={{
                            cursor: 'pointer',
                            p: 0.5,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: 'action.hover' },
                            opacity: zoomLevel <= 0.25 ? 0.3 : 1
                        }}
                    >
                        <ZoomOutIcon fontSize="small" />
                    </Box>
                    <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'center' }}>
                        {Math.round(zoomLevel * 100)}%
                    </Typography>
                    <Box
                        onClick={handleZoomIn}
                        sx={{
                            cursor: 'pointer',
                            p: 0.5,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: 'action.hover' },
                            opacity: zoomLevel >= 4 ? 0.3 : 1
                        }}
                    >
                        <ZoomInIcon fontSize="small" />
                    </Box>
                </Stack>
            </Box>

            <Box sx={{ position: 'relative', maxWidth: 1200, mx: 'auto' }}>
                {/* Central Timeline */}
                <Box 
                    ref={timelineRef}
                    sx={{ 
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 4,
                        height: '100%',
                        backgroundColor: theme.palette.divider,
                        zIndex: 1
                    }}
                />

                {/* Timeline Dates */}
                <Box sx={{ position: 'relative', minHeight: 365 * daySpacing, overflowY: 'auto', maxHeight: '80vh' }}>
                    {timelineDates.map((date, index) => {
                        const eventAtDate = getEventAtDate(date);
                        const isCurrentDate = isToday(date);
                        const isSundayDate = isSunday(date);
                        const isPastDateValue = isPastDate(date);
                        
                        return (
                            <Box 
                                key={date}
                                data-date={date}
                                sx={{ 
                                    position: 'absolute',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    top: index * daySpacing,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 120,
                                    height: Math.max(daySpacing, 15),
                                    zIndex: 2,
                                    opacity: isPastDateValue ? 0.4 : 1 // Fade past dates
                                }}
                            >
                                {/* Date Circle */}
                                <Box
                                    sx={{
                                        width: isSundayDate ? 16 : 12, // Bigger circles for Sundays
                                        height: isSundayDate ? 16 : 12,
                                        borderRadius: '50%',
                                        backgroundColor: isPastDateValue
                                            ? theme.palette.grey[400]
                                            : isCurrentDate 
                                                ? theme.palette.error.main 
                                                : eventAtDate 
                                                    ? eventAtDate.color 
                                                    : isSundayDate 
                                                        ? theme.palette.primary.main
                                                        : theme.palette.grey[300],
                                        border: `2px solid ${theme.palette.background.paper}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        boxShadow: (isSundayDate || isCurrentDate) && !isPastDateValue ? theme.shadows[2] : 'none'
                                    }}
                                />
                                
                                {/* Date Label - only show for Sundays, events, and today */}
                                {(isSundayDate || eventAtDate || isCurrentDate) && (
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            position: 'absolute',
                                            left: 25,
                                            fontWeight: isSundayDate || isCurrentDate ? 700 : 500,
                                            color: isCurrentDate 
                                                ? theme.palette.error.main 
                                                : isSundayDate 
                                                    ? theme.palette.primary.main
                                                    : 'text.primary',
                                            backgroundColor: theme.palette.background.paper,
                                            px: 0.5,
                                            py: 0.25,
                                            borderRadius: 0.5,
                                            fontSize: '0.75rem',
                                            boxShadow: theme.shadows[1],
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {formatDate(date)}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                    
                    {/* Events positioned alternating left and right */}
                    {campaignEvents.map((event, index) => {
                        const isDragging = draggedEvent?.id === event.id;
                        const hasDate = !!event.value;
                        const isLeftSide = index % 2 === 0;
                        const eventDateIndex = timelineDates.findIndex(date => date === event.value);
                        const topPosition = eventDateIndex >= 0 ? eventDateIndex * daySpacing : index * 80 * zoomLevel;
                        const isEventInPast = hasDate && isPastDate(event.value!);
                        
                        return (
                            <Box
                                key={event.id}
                                sx={{
                                    position: 'absolute',
                                    top: topPosition,
                                    left: isLeftSide ? '5%' : '55%',
                                    width: '40%',
                                    zIndex: 3
                                }}
                            >
                                <Paper
                                    sx={{
                                        p: 1.5,
                                        border: `2px solid ${isEventInPast ? theme.palette.grey[400] : event.color}`,
                                        borderRadius: 2,
                                        cursor: isEventInPast ? 'not-allowed' : 'grab',
                                        opacity: isDragging ? 0.5 : isEventInPast ? 0.6 : 1,
                                        transform: isDragging ? 'scale(0.95)' : 'scale(1)',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        minWidth: 120,
                                        '&:hover': {
                                            boxShadow: isEventInPast ? 'none' : theme.shadows[6],
                                            transform: isEventInPast ? 'scale(1)' : 'scale(1.02)'
                                        },
                                        '&:active': {
                                            cursor: isEventInPast ? 'not-allowed' : 'grabbing'
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            top: '50%',
                                            [isLeftSide ? 'right' : 'left']: -6,
                                            transform: 'translateY(-50%)',
                                            width: 0,
                                            height: 0,
                                            borderTop: '6px solid transparent',
                                            borderBottom: '6px solid transparent',
                                            [isLeftSide ? 'borderRight' : 'borderLeft']: `6px solid ${isEventInPast ? theme.palette.grey[400] : event.color}`
                                        }
                                    }}
                                    onMouseDown={isEventInPast ? undefined : (e) => handleMouseDown(e, event)}
                                >
                                    <Stack 
                                        direction={isLeftSide ? 'row' : 'row-reverse'} 
                                        spacing={1.5} 
                                        alignItems="center"
                                        sx={{ justifyContent: 'center' }}
                                    >
                                        {/* Event Icon */}
                                        <Box sx={{ color: event.color, fontSize: '1.2rem' }}>
                                            {event.icon}
                                        </Box>
                                        
                                        {/* Event Title and Date */}
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: event.color, 
                                                    fontWeight: 600,
                                                    fontSize: '0.875rem',
                                                    lineHeight: 1.2
                                                }}
                                            >
                                                {event.label}
                                            </Typography>
                                            <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                    color: 'text.secondary',
                                                    fontSize: '0.7rem',
                                                    display: 'block',
                                                    mt: 0.25
                                                }}
                                            >
                                                {hasDate ? formatDate(event.value!) : 'No date'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* Floating dragged event */}
            {draggedEvent && dragPosition && (
                <Paper
                    sx={{
                        position: 'fixed',
                        left: dragPosition.x - 120,
                        top: dragPosition.y - 35,
                        p: 1.5,
                        border: `2px solid ${draggedEvent.color}`,
                        borderRadius: 2,
                        backgroundColor: 'background.paper',
                        boxShadow: theme.shadows[8],
                        zIndex: 1000,
                        pointerEvents: 'none',
                        transform: 'rotate(-5deg)',
                        minWidth: 200
                    }}
                >
                    <Stack spacing={0.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box sx={{ color: draggedEvent.color }}>
                                {draggedEvent.icon}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {draggedEvent.label}
                            </Typography>
                        </Stack>
                        
                        {/* Real-time preview date */}
                        {dragPreviewDate && (
                            <Box sx={{ 
                                backgroundColor: isPastDate(dragPreviewDate) 
                                    ? theme.palette.error.light 
                                    : theme.palette.success.light,
                                color: isPastDate(dragPreviewDate) 
                                    ? theme.palette.error.contrastText 
                                    : theme.palette.success.contrastText,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                textAlign: 'center'
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {isPastDate(dragPreviewDate) ? '⚠️ Past Date' : '📅'} {new Date(dragPreviewDate).toLocaleDateString('en-US', {
                                        weekday: 'short',
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric'
                                    })}
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>
            )}

            {/* Success Snackbar */}
            <Snackbar 
                open={showSuccess} 
                autoHideDuration={3000} 
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setShowSuccess(false)} 
                    severity="success" 
                    sx={{ width: '100%' }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar 
                open={showError} 
                autoHideDuration={6000} 
                onClose={() => setShowError(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setShowError(false)} 
                    severity="error" 
                    sx={{ width: '100%' }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default TimelineEventsDates;
